import * as next from 'next'

import * as entity from '@boutique/entities'
import * as cold from '@owl-nest/cold-data'
import { type ArticleListProps } from '@bob/article'
import * as hooks from '@bob/hooks'
import * as service from '@bob/services'

export { ArticleList as default } from '@bob/article'

export async function getStaticProps({
  locale = cold.defaultLocale.code,
}: next.GetStaticPropsContext<Record<never, never>>): Promise<next.GetStaticPropsResult<ArticleListProps>> {
  if (locale !== 'fr') {
    return {
      notFound: true,
    }
  }

  const articles = await service.article.build.getAllMedium(locale)

  const environmentalPillar = cold.taxonomies[cold.types['environmental-pillar']]
  const moments = cold.taxonomies[cold.types['moment']]
  const socialPillar = cold.taxonomies[cold.types['social-pillar']]

  const blogIndex = hooks.useSearchEngine.index(articles, {
    identifier: (article) => article.slug,
    indexes: {
      section: {
        title: 'Rubrique',
        indexer: (article) => [
          {
            hash: article.section,
            title: article.section,
          },
        ],
      },
      categories: {
        title: 'Catégories',
        indexer: (article) => [
          {
            hash: article.category.slug,
            title: article.category.name,
          },
        ],
      },
      socialPillar: {
        title: socialPillar.name,
        indexer: (article) =>
          article.pillars.social.map((tag) => ({
            hash: tag.slug,
            title: tag.name,
          })),
      },
      environmentalPillar: {
        title: environmentalPillar.name,
        indexer: (article) =>
          article.pillars.environmental.map((tag) => ({
            hash: tag.slug,
            title: tag.name,
          })),
      },
      moments: {
        title: moments.name,
        indexer: (article) =>
          article.moments.map((moment) => ({
            hash: moment.slug,
            title: moment.name,
          })),
      },
    },
  })

  return {
    props: {
      articles,
      brands: Object.values(cold.brands)
        .filter(
          (
            brand,
          ): brand is Omit<entity.brand.ColdBrand, 'firstPublishedAt'> & {
            firstPublishedAt: string
          } => brand.firstPublishedAt !== null,
        )
        .sort((brandA, brandB) => {
          if (brandA.highlighted && !brandB.highlighted) {
            return -1
          }

          if (!brandA.highlighted && brandB.highlighted) {
            return 1
          }

          if (brandA.firstPublishedAt === brandB.firstPublishedAt) {
            return 0
          }

          return new Date(brandB.firstPublishedAt).getTime() - new Date(brandA.firstPublishedAt).getTime()
        })
        .filter((_, i) => i < 4),
      index: blogIndex,
    },
    revalidate: 10,
  }
}
